/**
 * TableSkeleton - Animated skeleton loader for tables
 * 
 * Shows a table-shaped skeleton with shimmer effect while
 * table data is being generated. Creates visual continuity
 * with the final rendered table.
 * 
 * @param {string} caption - Optional caption to display above skeleton
 * @param {number} rows - Number of skeleton rows to show (default: 4)
 * @param {number} columns - Number of columns to show (default: 4)
 */
export function TableSkeleton({ caption, rows = 4, columns = 4 }) {
  return (
    <div className="my-4">
      {/* Caption with subtle loading indicator */}
      {caption && (
        <div className="mb-2 flex items-center gap-2">
          <span className="font-semibold text-gray-700">{caption}</span>
          <span className="text-sm text-gray-400">generating...</span>
        </div>
      )}
      {!caption && (
        <div className="mb-2 flex items-center gap-2">
          <span className="text-sm text-gray-500">Generating table...</span>
        </div>
      )}
      
      {/* Skeleton table */}
      <div className="overflow-hidden rounded-lg border border-gray-200">
        {/* Header row */}
        <div className="flex border-b border-gray-200 bg-gray-50">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <div 
              key={`header-${colIndex}`} 
              className="flex-1 border-r border-gray-200 p-3 last:border-r-0"
            >
              <div className="h-4 rounded bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] animate-shimmer" />
            </div>
          ))}
        </div>
        
        {/* Data rows */}
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div 
            key={`row-${rowIndex}`} 
            className="flex border-b border-gray-100 last:border-b-0"
          >
            {Array.from({ length: columns }).map((_, colIndex) => (
              <div 
                key={`cell-${rowIndex}-${colIndex}`} 
                className="flex-1 border-r border-gray-100 p-3 last:border-r-0"
              >
                <div 
                  className="h-3 rounded bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] animate-shimmer"
                  style={{ 
                    width: `${60 + Math.random() * 35}%`,
                    animationDelay: `${(rowIndex * columns + colIndex) * 50}ms`
                  }}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
